import React, { useState, useEffect, useMemo } from 'react';
import { Form, TextArea, Dropdown, Button, Icon, Segment, Label, Modal } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import AsyncPageContent from '../../../../common/components/page/AsyncPageContent';
import { Title } from '@/common/styles/page';
import { StyledSaveButton, StyledButton } from '@/common/components/button/StyledButton';
import {
  useGetPromptTypesQuery,
  useGetPromptQuery,
  useUpdatePromptMutation,
  usePreviewPromptMutation,
  useTestPromptMutation,
  PromptTestResult,
  PromptPreviewResult,
} from '../api/aiPromptApi';
import {
  useGetRAGInstructionsQuery,
  useUpdateRAGInstructionsMutation,
  useTestRAGInstructionsMutation,
} from '../api/ragInstructionsApi';
import { AIPromptEvalModal, EvalStatusLabel } from './AIPromptEvalModal';
import { useLatestEval } from '../hooks/useLatestEval';
import { EvalSubject } from '../api/aiPromptApi';
import styled from 'styled-components';

const EditorContainer = styled.div`
  max-width: 1200px;
  margin: 20px 0;
`;

const MonoTextArea = styled(TextArea as any)`
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace !important;
  font-size: 13px !important;
  line-height: 1.6 !important;
  white-space: pre !important;
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: #666;
  text-align: right;
`;

const LastUpdated = styled.div`
  font-size: 12px;
  color: #999;
  font-style: italic;
`;

const DropdownContainer = styled.div`
  margin-bottom: 20px;
  max-width: 300px;
`;

const TestResultContainer = styled.div`
  margin-top: 15px;
`;

const ResponseBox = styled(Segment)`
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace !important;
  font-size: 13px !important;
  line-height: 1.6 !important;
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  max-height: 400px;
  overflow-y: auto;
  background-color: #f8f9fa !important;
`;

const PromptPreviewBox = styled(Segment)`
  font-family: 'Monaco', 'Consolas', 'Courier New', monospace !important;
  font-size: 12px !important;
  line-height: 1.5 !important;
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
  max-height: 200px;
  overflow-y: auto;
  background-color: #f5f5f0 !important;
  color: #555 !important;
`;

const MetricsRow = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const FieldLabel = styled.label`
  font-weight: bold;
  font-size: 13px;
  margin-bottom: 4px;
  display: block;
  color: #333;
`;

const FieldDescription = styled.div`
  font-size: 12px;
  color: #888;
  margin-bottom: 6px;
`;

const RAG_KEY = 'rag';
const RAG_MAX_LENGTH = 10000;
const FIELD_MAX_LENGTH = 20000;

export const AIPromptEditor = () => {
  const [selectedKey, setSelectedKey] = useState<string>(RAG_KEY);
  const { addToast } = useToasts();

  // --- Prompt API hooks ---
  const { data: promptTypes, isLoading: typesLoading } = useGetPromptTypesQuery();

  const selectedPromptType = selectedKey !== RAG_KEY ? Number(selectedKey) : null;

  const {
    data: promptData,
    error: promptError,
    isLoading: promptLoading,
    isFetching: promptFetching,
  } = useGetPromptQuery(selectedPromptType!, {
    skip: selectedPromptType === null,
  });

  const [updatePrompt, { isLoading: isSavingPrompt }] = useUpdatePromptMutation();
  const [previewPrompt, { isLoading: isLoadingPreview }] = usePreviewPromptMutation();
  const [testPrompt, { isLoading: isTestingPrompt }] = useTestPromptMutation();

  // --- RAG API hooks ---
  const {
    data: ragData,
    error: ragError,
    isLoading: ragLoading,
  } = useGetRAGInstructionsQuery(undefined, {
    skip: selectedKey !== RAG_KEY,
  });

  const [updateRAG, { isLoading: isSavingRAG }] = useUpdateRAGInstructionsMutation();
  const [testRAG, { isLoading: isTestingRAG }] = useTestRAGInstructionsMutation();

  // --- Editor state ---
  const [ragText, setRagText] = useState('');
  const [originalRagText, setOriginalRagText] = useState('');
  const [ragFollowupText, setRagFollowupText] = useState('');
  const [originalRagFollowupText, setOriginalRagFollowupText] = useState('');

  const [instructions, setInstructions] = useState('');
  const [example, setExample] = useState('');
  const [originalInstructions, setOriginalInstructions] = useState('');
  const [originalExample, setOriginalExample] = useState('');

  // --- Test state ---
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState<PromptTestResult | null>(null);
  const [previewData, setPreviewData] = useState<PromptPreviewResult | null>(null);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [mockDataExpanded, setMockDataExpanded] = useState(false);
  const [outputSchemaExpanded, setOutputSchemaExpanded] = useState(false);

  const isRAG = selectedKey === RAG_KEY;
  const isTesting = isTestingPrompt || isTestingRAG;

  // --- Eval state (badge on the Evals button; details live in the modal) ---
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const evalSubject: EvalSubject | null = isRAG
    ? { kind: 'rag' }
    : selectedPromptType !== null
      ? { kind: 'prompt', promptType: selectedPromptType }
      : null;
  const { run: latestEvalRun, data: latestEvalData } = useLatestEval(evalSubject);
  const evalSupported = latestEvalData?.supported ?? true;

  // Load data when selection changes
  useEffect(() => {
    if (isRAG) {
      const t = ragData?.instructions_text || '';
      setRagText(t);
      setOriginalRagText(t);
      const ft = ragData?.followup_instructions_text || '';
      setRagFollowupText(ft);
      setOriginalRagFollowupText(ft);
    } else {
      const i = promptData?.instructions || '';
      const e = promptData?.example || '';
      setInstructions(i);
      setExample(e);
      setOriginalInstructions(i);
      setOriginalExample(e);
    }
  }, [selectedKey, ragData, promptData, isRAG]);

  // Reset test state when selection changes
  useEffect(() => {
    setTestResult(null);
    setTestMessage('');
    setPreviewData(null);
    setPromptExpanded(false);
    setMockDataExpanded(false);
    setOutputSchemaExpanded(false);
  }, [selectedKey]);

  // --- Dropdown options ---
  const dropdownOptions = useMemo(() => {
    const options = [
      { key: RAG_KEY, text: 'Copilot RAG Instructions', value: RAG_KEY },
    ];
    if (promptTypes) {
      for (const type of promptTypes) {
        options.push({
          key: String(type.value),
          text: type.label,
          value: String(type.value),
        });
      }
    }
    return options;
  }, [promptTypes]);

  // --- Derived state ---
  const isDirty = isRAG
    ? ragText !== originalRagText || ragFollowupText !== originalRagFollowupText
    : instructions !== originalInstructions || example !== originalExample;

  const hasContent = isRAG
    ? !!ragText.trim()
    : !!(instructions.trim() || example.trim());

  const totalLength = isRAG
    ? ragText.length + ragFollowupText.length
    : instructions.length + example.length;

  const maxLength = isRAG ? RAG_MAX_LENGTH : FIELD_MAX_LENGTH;
  const isOverLimit = isRAG
    ? ragText.length > maxLength || ragFollowupText.length > maxLength
    : false;

  const isSaving = isSavingRAG || isSavingPrompt;
  const isFetching = promptFetching;
  const isLoading = typesLoading || (isRAG ? ragLoading : promptLoading);
  const error = isRAG ? ragError : promptError;

  const updatedAt = isRAG ? ragData?.updated_at : promptData?.updated_at;
  const updatedBy = isRAG ? ragData?.updated_by : promptData?.updated_by;
  const version = isRAG ? ragData?.version : promptData?.version;

  // --- Handlers ---
  const handleOpenTestModal = async () => {
    setTestModalOpen(true);
    setTestResult(null);
    setPromptExpanded(true);
    setMockDataExpanded(false);

    // For non-RAG types, load preview automatically
    if (!isRAG && selectedPromptType !== null) {
      try {
        const result = await previewPrompt({
          promptType: selectedPromptType,
          ...(isDirty ? { instructions, example } : {}),
        }).unwrap();
        setPreviewData(result);
      } catch (err) {
        console.error('Failed to load preview:', err);
      }
    }
  };

  const handleSave = async () => {
    if (!hasContent) {
      addToast('Prompt text cannot be empty', { appearance: 'error' });
      return;
    }

    try {
      if (isRAG) {
        if (ragText.length > maxLength || ragFollowupText.length > maxLength) {
          addToast(`Text exceeds maximum length of ${maxLength.toLocaleString()} characters`, { appearance: 'error' });
          return;
        }
        await updateRAG({ instructions_text: ragText, followup_instructions_text: ragFollowupText }).unwrap();
        setOriginalRagText(ragText);
        setOriginalRagFollowupText(ragFollowupText);
      } else {
        await updatePrompt({
          promptType: selectedPromptType!,
          instructions,
          example,
        }).unwrap();
        setOriginalInstructions(instructions);
        setOriginalExample(example);
      }
      addToast('Saved successfully! Cache has been reloaded.', { appearance: 'success' });
    } catch (err) {
      console.error('Failed to save:', err);
      addToast('Failed to save. Please try again.', { appearance: 'error' });
    }
  };

  const handleTest = async () => {
    try {
      let result: PromptTestResult;

      if (isRAG) {
        if (!testMessage.trim()) {
          addToast('Please enter a test question', { appearance: 'error' });
          return;
        }
        result = await testRAG({
          test_message: testMessage,
          instructions_text: ragText,
        }).unwrap();
      } else {
        if (selectedPromptType === null) return;
        // Send current editor values so the backend uses them
        // via the real service prompt pipeline with mock data
        result = await testPrompt({
          promptType: selectedPromptType,
          ...(isDirty ? { instructions, example } : {}),
        }).unwrap();
      }

      setTestResult(result);
      if (result.success) {
        addToast('Test completed successfully!', { appearance: 'success' });
      } else {
        addToast(`Test failed: ${result.error}`, { appearance: 'error' });
      }
    } catch (err: any) {
      console.error('Failed to run test:', err);
      addToast(`Test failed: ${err?.data?.error || 'Unknown error'}`, { appearance: 'error' });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canRunTest = isRAG
    ? !isTesting && !!testMessage.trim() && hasContent
    : !isTesting && hasContent;

  const selectedLabel = dropdownOptions.find(o => o.value === selectedKey)?.text || 'Prompt';

  return (
    <AsyncPageContent isLoading={isLoading} error={error} data-testid="ai-prompt-editor">
      <EditorContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title data-testid="ai-prompt-header">AI Prompts</Title>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {evalSubject && evalSupported && (
              <Button
                basic
                onClick={() => setEvalModalOpen(true)}
                data-testid="open-evals-button"
                style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}
              >
                <Icon name="clipboard check" /> Evals
                <EvalStatusLabel run={latestEvalRun} size="mini" />
              </Button>
            )}
            <StyledButton
              label="Test"
              color="blue"
              icon={<Icon name="play" style={{ color: 'white', marginTop: '-6px' }} />}
              width="100px"
              onClick={handleOpenTestModal}
              disabled={!hasContent}
            />
            <StyledSaveButton
              label="Save"
              onClick={handleSave}
              disabled={isSaving || isOverLimit || !hasContent || !isDirty || isFetching}
            />
          </div>
        </div>

        <DropdownContainer>
          <Form.Field>
            <label>Prompt Type</label>
            <Dropdown
              selection
              fluid
              options={dropdownOptions}
              value={selectedKey}
              onChange={(_e: any, data: any) => setSelectedKey(data.value as string)}
              loading={typesLoading}
              disabled={typesLoading || isSaving}
              data-testid="prompt-type-dropdown"
            />
          </Form.Field>
        </DropdownContainer>

        <Form loading={isFetching}>
          {/* RAG mode: two text areas for initial + follow-up instructions */}
          {isRAG && (
            <>
              <Form.Field>
                <FieldLabel>Initial Analysis Instructions</FieldLabel>
                <FieldDescription>
                  Instructions used for the first response in a conversation. Defines section structure, tone, and formatting.
                </FieldDescription>
                <MonoTextArea
                  value={ragText}
                  onChange={(e: any) => setRagText(e.target.value)}
                  rows={20}
                  placeholder="Enter RAG instructions here..."
                  style={{ width: '100%', minHeight: '300px' }}
                  data-testid="prompt-textarea"
                />
                <CharacterCount style={{ color: ragText.length > maxLength ? '#e5657a' : '#666' }}>
                  {ragText.length.toLocaleString()} / {maxLength.toLocaleString()} characters
                </CharacterCount>
              </Form.Field>

              <Form.Field style={{ marginTop: '16px' }}>
                <FieldLabel>Follow-up Turn Instructions</FieldLabel>
                <FieldDescription>
                  Instructions used for follow-up responses in multi-turn conversations. Leave empty to use the built-in default
                  (focused analysis without repeating the initial template structure).
                </FieldDescription>
                <MonoTextArea
                  value={ragFollowupText}
                  onChange={(e: any) => setRagFollowupText(e.target.value)}
                  rows={10}
                  placeholder="Leave empty to use built-in default. When empty, follow-up responses will focus on the user's specific question without repeating the initial analysis template."
                  style={{ width: '100%', minHeight: '150px' }}
                  data-testid="followup-textarea"
                />
                <CharacterCount style={{ color: ragFollowupText.length > maxLength ? '#e5657a' : '#666' }}>
                  {ragFollowupText.length.toLocaleString()} / {maxLength.toLocaleString()} characters
                </CharacterCount>
              </Form.Field>
            </>
          )}

          {/* Prompt type mode: static preamble + 2 editable fields */}
          {!isRAG && (
            <>
              <Form.Field>
                <FieldLabel>JSON Preamble (static)</FieldLabel>
                <FieldDescription>This preamble is prepended to every AI prompt automatically. It is not editable.</FieldDescription>
                <PromptPreviewBox style={{ maxHeight: '120px' }}>
                  {'Your task is to return a clean, undecorated JSON structure that can be parsed into code.\n'
                  + 'Do not include any additional text, explanations, or comments outside of the JSON.\n\n'
                  + 'Instructions:\n'
                  + '1. Do not include any additional information in the response.\n'
                  + '2. Return only the JSON structure. No comments, no introductions, no additional explanations.\n'
                  + '3. Ensure the JSON is valid and can be directly parsed by a JSON parser.'}
                </PromptPreviewBox>
              </Form.Field>

              <Form.Field style={{ marginTop: '16px' }}>
                <FieldLabel>Instructions</FieldLabel>
                <FieldDescription>Format specification, rules, and output requirements</FieldDescription>
                <MonoTextArea
                  value={instructions}
                  onChange={(e: any) => setInstructions(e.target.value)}
                  rows={12}
                  placeholder="Enter format instructions and rules..."
                  style={{ width: '100%', minHeight: '180px' }}
                  data-testid="instructions-textarea"
                />
              </Form.Field>

              <Form.Field style={{ marginTop: '16px' }}>
                <FieldLabel>Example Output</FieldLabel>
                <FieldDescription>Example JSON output that demonstrates the expected format</FieldDescription>
                <MonoTextArea
                  value={example}
                  onChange={(e: any) => setExample(e.target.value)}
                  rows={6}
                  placeholder="Enter example JSON output..."
                  style={{ width: '100%', minHeight: '100px' }}
                  data-testid="example-textarea"
                />
              </Form.Field>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
            <CharacterCount style={{ color: isOverLimit ? '#e5657a' : '#666' }}>
              {totalLength.toLocaleString()} characters total
              {isOverLimit && ' (exceeds limit)'}
            </CharacterCount>

            {updatedAt && (
              <LastUpdated>
                Last updated: {formatDate(updatedAt)}
                {version && ` (v${version})`}
                {updatedBy && ` by User ID: ${updatedBy}`}
              </LastUpdated>
            )}
          </div>
        </Form>

        {/* Eval modal. Proposals load as unsaved edits — the admin still has to Save
            (which marks the eval stale) and re-run. */}
        {evalSubject && (
          <AIPromptEvalModal
            open={evalModalOpen}
            onClose={() => setEvalModalOpen(false)}
            subject={evalSubject}
            subjectLabel={selectedLabel}
            promptSaved={isRAG ? !!ragData?.id : !!promptData?.id}
            currentSections={
              isRAG
                ? { instructions_text: originalRagText, followup_instructions_text: originalRagFollowupText }
                : { instructions: originalInstructions, example: originalExample }
            }
            editorDirty={isDirty}
            onApplyProposal={(sections) => {
              if (isRAG) {
                if (sections.instructions_text !== undefined) setRagText(sections.instructions_text);
                if (sections.followup_instructions_text !== undefined) setRagFollowupText(sections.followup_instructions_text);
              } else {
                if (sections.instructions !== undefined) setInstructions(sections.instructions);
                if (sections.example !== undefined) setExample(sections.example);
              }
            }}
          />
        )}

        {/* Test Prompt Modal */}
        <Modal
          open={testModalOpen}
          onClose={() => setTestModalOpen(false)}
          size="large"
          data-testid="test-prompt-modal"
        >
          <Modal.Header>
            Test Prompt &mdash; {selectedLabel}
          </Modal.Header>
          <Modal.Content scrolling>
            {isRAG ? (
              <>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 15 }}>
                  Enter a question below. The test will use your current editor text as the RAG instructions.
                </p>
                <Form>
                  <Form.Field>
                    <label>Test Question</label>
                    <TextArea
                      value={testMessage}
                      onChange={(e: any) => setTestMessage(e.target.value)}
                      rows={3}
                      placeholder="Enter a question to ask the copilot (e.g., 'What are the main themes in Engineering?')..."
                      style={{ width: '100%' }}
                      data-testid="test-message-input"
                    />
                  </Form.Field>
                </Form>
              </>
            ) : (
              <>
                {isDirty && (
                  <Label color="yellow" size="small" style={{ marginBottom: 10 }}>
                    Using unsaved editor changes
                  </Label>
                )}

                {/* Assembled prompt — shown immediately when modal opens */}
                <div>
                  <span
                    style={{ color: '#4183c4', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}
                    onClick={() => setPromptExpanded(!promptExpanded)}
                  >
                    <Icon name={promptExpanded ? 'caret down' : 'caret right'} />
                    Assembled Prompt {isLoadingPreview && '(loading...)'}
                  </span>
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                    Everything sent to ChatGPT
                  </span>
                  {promptExpanded && previewData?.prompt && (
                    <PromptPreviewBox style={{ marginTop: 8, maxHeight: '400px' }} data-testid="test-prompt-preview">
                      {'═══ SYSTEM MESSAGE ═══\n'}
                      {previewData.system_message}
                      {'\n\n═══ USER MESSAGE ═══\n'}
                      {previewData.prompt}
                    </PromptPreviewBox>
                  )}
                  {promptExpanded && !previewData && !isLoadingPreview && (
                    <Segment style={{ color: '#999', fontStyle: 'italic' }}>
                      No preview available for this prompt type.
                    </Segment>
                  )}
                </div>

                {/* Mock data — collapsible */}
                <div style={{ marginTop: 15 }}>
                  <span
                    style={{ color: '#4183c4', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}
                    onClick={() => setMockDataExpanded(!mockDataExpanded)}
                  >
                    <Icon name={mockDataExpanded ? 'caret down' : 'caret right'} />
                    Mock Assessment Data
                  </span>
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                    The test data injected into the prompt pipeline
                  </span>
                  {mockDataExpanded && previewData?.mock_data && (
                    <PromptPreviewBox style={{ marginTop: 8, maxHeight: '400px' }} data-testid="mock-data-preview">
                      {JSON.stringify(previewData.mock_data, null, 2)}
                    </PromptPreviewBox>
                  )}
                  {mockDataExpanded && !previewData?.mock_data && !isLoadingPreview && (
                    <Segment style={{ color: '#999', fontStyle: 'italic' }}>
                      No mock data available for this prompt type.
                    </Segment>
                  )}
                </div>

                {/* Output schema — shows the deterministic wrapper shape */}
                <div style={{ marginTop: 15 }}>
                  <span
                    style={{ color: '#4183c4', cursor: 'pointer', fontSize: 13, fontWeight: 'bold' }}
                    onClick={() => setOutputSchemaExpanded(!outputSchemaExpanded)}
                  >
                    <Icon name={outputSchemaExpanded ? 'caret down' : 'caret right'} />
                    Service Output Schema
                  </span>
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 8 }}>
                    Deterministic values added after AI responds
                  </span>
                  {outputSchemaExpanded && previewData?.output_schema && (
                    <PromptPreviewBox style={{ marginTop: 8, maxHeight: '400px' }} data-testid="output-schema-preview">
                      {JSON.stringify(previewData.output_schema, null, 2)}
                    </PromptPreviewBox>
                  )}
                  {outputSchemaExpanded && !previewData?.output_schema && !isLoadingPreview && (
                    <Segment style={{ color: '#999', fontStyle: 'italic' }}>
                      No output schema available for this prompt type.
                    </Segment>
                  )}
                </div>
              </>
            )}

            {testResult && (
              <TestResultContainer>
                <h5 style={{ marginBottom: 10 }}>
                  {testResult.success ? (
                    <span style={{ color: '#21ba45' }}>
                      <Icon name="check circle" /> Test Successful
                    </span>
                  ) : (
                    <span style={{ color: '#db2828' }}>
                      <Icon name="times circle" /> Test Failed
                    </span>
                  )}
                </h5>

                {testResult.success && testResult.response && (
                  <>
                    {testResult.wrapped_response && (
                      <>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 5 }}>
                          Service Output:
                        </label>
                        <ResponseBox data-testid="wrapped-response">
                          {JSON.stringify(testResult.wrapped_response, null, 2)}
                        </ResponseBox>
                      </>
                    )}

                    <MetricsRow>
                      {testResult.usage && (
                        <>
                          <Label>
                            <Icon name="file text" />
                            Prompt: {testResult.usage.prompt_tokens} tokens
                          </Label>
                          <Label>
                            <Icon name="comment" />
                            Completion: {testResult.usage.completion_tokens} tokens
                          </Label>
                          <Label>
                            <Icon name="calculator" />
                            Total: {testResult.usage.total_tokens} tokens
                          </Label>
                        </>
                      )}
                      <Label>
                        <Icon name="clock" />
                        Duration: {testResult.duration_ms}ms
                      </Label>
                      {testResult.model && (
                        <Label>
                          <Icon name="microchip" />
                          Model: {testResult.model}
                        </Label>
                      )}
                    </MetricsRow>
                  </>
                )}

                {!testResult.success && testResult.error && (
                  <Segment color="red">
                    <strong>Error:</strong> {testResult.error}
                  </Segment>
                )}
              </TestResultContainer>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setTestModalOpen(false)}>
              Close
            </Button>
            <Button
              primary
              onClick={handleTest}
              loading={isTesting}
              disabled={!canRunTest}
              data-testid="run-test-button"
            >
              <Icon name="play" />
              Run Test
            </Button>
          </Modal.Actions>
        </Modal>
      </EditorContainer>
    </AsyncPageContent>
  );
};
